/** 录音只留在当前对话框内；关闭、换照片和进入后台都会归还麦克风和音频 URL。 */
import type { VoiceDto } from '@star-sea/shared';
import { el } from './dom.ts';
import type { Sound } from './sound.ts';
import { createVoiceRecorder, type VoiceState } from './voice.ts';

export class VoiceConflict extends Error {
  readonly base: string | null;
  constructor(message: string, base: string | null) { super(message); this.base = base; }
}

interface VoiceOptions {
  mine: boolean;
  name: string;
  voice: VoiceDto | null;
  online: boolean;
  sound: Sound;
  load(signal: AbortSignal): Promise<Blob>;
  save(blob: Blob, base: string | null, signal: AbortSignal): Promise<void>;
  remove(base: string | null, signal: AbortSignal): Promise<void>;
  onClose(): void;
}

export function voiceDialog(o: VoiceOptions, createRecorder: typeof createVoiceRecorder = createVoiceRecorder): { close(): void } {
  const dialog = el('dialog', { class: 'dialog voice-dialog', attrs: { 'aria-label': o.mine ? '我的声音' : `${o.name}的声音` } });
  const title = el('h2', { class: 'dialog-title', text: o.mine ? '把这一刻，说给 Ta 听' : `${o.name} 留下的声音` });
  const hint = el('p', { class: 'field-hint', text: o.mine ? '留 5–10 秒的话，试听后再保存。录音时音乐会静下来；只录音不会揭晓互盲的另一面。' : '点播放，听听那天留下的话。' });
  const status = el('p', { class: 'voice-status', attrs: { role: 'status', 'aria-live': 'polite' } });
  const error = el('p', { class: 'form-error', attrs: { role: 'alert' } });
  const audio = el('audio', { attrs: { preload: 'metadata' } });
  const record = el('button', { class: 'btn', text: '开始录音', attrs: { type: 'button' } });
  const stop = el('button', { class: 'btn', text: '停止录音', attrs: { type: 'button' } });
  const play = el('button', { class: 'btn', text: '播放', attrs: { type: 'button' } });
  const save = el('button', { class: 'btn btn-primary', text: '保存这段声音', attrs: { type: 'button' } });
  const remove = el('button', { class: 'btn btn-ghost', text: '删除已保存的录音', attrs: { type: 'button' } });
  const closeButton = el('button', { class: 'btn btn-ghost', text: '关闭', attrs: { type: 'button' } });
  dialog.append(title, hint, status, error, audio, el('div', { class: 'dialog-actions voice-actions' }, record, stop, play, save, remove, closeButton));
  let closed = false, busy = false, candidate: Blob | null = null, url: string | null = null;
  let base = o.voice?.updatedAt ?? null, loadRevision = 0;
  let releasePlaying: (() => void) | null = null;
  const controller = new AbortController();
  const stopPlaying = () => { audio.pause(); releasePlaying?.(); releasePlaying = null; play.textContent = '播放'; };
  const clearAudio = () => {
    stopPlaying();
    audio.removeAttribute('src');
    audio.load();
    if (url) URL.revokeObjectURL(url);
    url = null;
  };
  const setAudio = (blob: Blob) => { clearAudio(); url = URL.createObjectURL(blob); audio.src = url; };
  const controls = (state = recorder.state) => {
    const active = ['requesting', 'recording', 'stopping'].includes(state.phase);
    record.hidden = !o.mine || active;
    record.disabled = busy || !o.online;
    record.textContent = candidate ? '重新录音' : o.voice ? '重录一段' : '开始录音';
    stop.hidden = !active;
    stop.disabled = state.phase !== 'recording' || state.elapsedMs < 5000;
    play.hidden = !url || active;
    play.disabled = busy;
    save.hidden = !candidate || active;
    save.disabled = busy || !o.online;
    remove.hidden = !o.mine || !o.voice || active || !!candidate;
    remove.disabled = busy || !o.online;
    dialog.setAttribute('aria-busy', String(busy));
  };
  const onChange = (state: VoiceState) => {
    if (closed) return;
    if (state.phase === 'requesting') status.textContent = '请允许使用麦克风…';
    else if (state.phase === 'recording') status.textContent = `${Math.min(10, state.elapsedMs / 1000).toFixed(1)} / 10 秒${state.elapsedMs < 5000 ? ' · 至少录 5 秒' : ' · 可以停止了'}`;
    else if (state.phase === 'stopping') status.textContent = '正在整理这段声音…';
    else if (state.phase === 'ready' && state.clip) {
      candidate = state.clip.blob;
      setAudio(candidate);
      status.textContent = `录好了 ${(state.clip.durationMs / 1000).toFixed(1)} 秒，先试听，再保存。`;
    } else if (state.phase === 'error') { error.textContent = state.error ?? '录音失败，请重试'; status.textContent = ''; }
    controls(state);
  };
  const recorder = createRecorder({ onChange, holdSound: () => o.sound.hold('recording') });
  const close = () => {
    if (closed) return;
    closed = true;
    controller.abort();
    loadRevision++;
    recorder.dispose();
    clearAudio();
    candidate = null;
    document.removeEventListener('visibilitychange', hidden);
    dialog.close();
    dialog.remove();
    o.onClose();
  };
  const hidden = () => { if (document.hidden) close(); };
  const work = async (fn: () => Promise<void>) => {
    if (busy || closed) return;
    busy = true; error.textContent = ''; stopPlaying(); controls();
    try { await fn(); if (!closed) close(); }
    catch (e) {
      if (closed) return;
      if (e instanceof VoiceConflict) base = e.base;
      error.textContent = e instanceof Error ? e.message : String(e);
    } finally { busy = false; if (!closed) controls(); }
  };
  record.addEventListener('click', () => {
    loadRevision++;
    error.textContent = ''; candidate = null; clearAudio();
    void recorder.start();
  });
  stop.addEventListener('click', () => recorder.stop());
  play.addEventListener('click', () => {
    if (!audio.paused) return stopPlaying();
    void audio.play().catch(() => { if (!closed) error.textContent = '播放失败，请重试或换一个支持音频的浏览器'; stopPlaying(); });
  });
  audio.addEventListener('play', () => { releasePlaying?.(); releasePlaying = o.sound.hold('voice'); play.textContent = '暂停'; });
  for (const event of ['ended', 'pause', 'error']) audio.addEventListener(event, () => { releasePlaying?.(); releasePlaying = null; play.textContent = '播放'; });
  save.addEventListener('click', () => { const blob = candidate; if (blob) void work(() => o.save(blob, base, controller.signal)); });
  remove.addEventListener('click', () => {
    if (remove.dataset.confirmed !== 'yes') {
      remove.dataset.confirmed = 'yes'; remove.textContent = '确认删除这段声音'; return;
    }
    void work(() => o.remove(base, controller.signal));
  });
  closeButton.addEventListener('click', close);
  dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
  dialog.addEventListener('close', close);
  document.addEventListener('visibilitychange', hidden);
  document.body.append(dialog);
  dialog.showModal();
  controls();
  if (!o.online) status.textContent = '现在离线，声音不缓存在本机；连上以后再听或录制。';
  else if (o.voice) {
    status.textContent = `正在载入 ${(o.voice.durationMs / 1000).toFixed(1)} 秒的声音…`;
    const revision = ++loadRevision;
    void o.load(controller.signal).then((blob) => {
      if (closed || revision !== loadRevision) return;
      setAudio(blob); status.textContent = `${(o.voice!.durationMs / 1000).toFixed(1)} 秒 · 点播放，听听这段声音`; controls();
    }).catch((e: unknown) => { if (!closed && revision === loadRevision) { status.textContent = ''; error.textContent = e instanceof Error ? e.message : String(e); } });
  }
  return { close };
}
